import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ERRORS } from '@/constants';
import { reviewLinksService } from './service';

/**
 * Server state for review links.
 *
 * The key lives here rather than in `services/queries/keys.ts` so the feature
 * stays in its own folder.
 */
export const reviewLinkKeys = {
  all: ['review-links'] as const,
  list: () => [...reviewLinkKeys.all, 'list'] as const,
  notes: (id: string) => [...reviewLinkKeys.all, 'notes', id] as const,
};

export function useReviewLinks() {
  return useQuery({
    queryKey: reviewLinkKeys.list(),
    queryFn: ({ signal }) => reviewLinksService.list(signal),
    // A dentist can open or answer a link at any moment, so not long - but
    // long enough that switching tabs back and forth is not a refetch each time.
    staleTime: 30_000,
  });
}

/**
 * A link's comments, fetched when its row is opened rather than with the list -
 * the list would otherwise carry every comment on every design to show none.
 * Skipped when the list already says there are none.
 */
export function useLinkNotes(id: string, enabled: boolean) {
  return useQuery({
    queryKey: reviewLinkKeys.notes(id),
    queryFn: ({ signal }) => reviewLinksService.notes(id, signal),
    enabled,
    staleTime: 30_000,
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : ERRORS.generic;
}

/** The three row actions share one shape: call, refresh the list, say so. */
function useLinkAction(action: (id: string) => Promise<unknown>, done: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: action,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reviewLinkKeys.all });
      toast.success(done);
    },
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  });
}

export function useExtendLink() {
  return useLinkAction((id) => reviewLinksService.extend(id), 'Link extended by 30 days.');
}

export function useResendLink() {
  return useLinkAction((id) => reviewLinksService.resend(id), 'Sent to the dentist again.');
}

export function useRevokeLink() {
  return useLinkAction(
    (id) => reviewLinksService.revoke(id),
    'Link revoked. The dentist can no longer open it.',
  );
}
